import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Input,
  Select,
  useToast,
} from '@adop/ui';
import { CreditCard, Wallet } from 'lucide-react';
import type { ExternalOrderLinks, StripeConnectResponse } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAuth } from '../../lib/auth-context';
import { useAsync } from '../../lib/useAsync';
import { getLocation, updateLocation } from '../../lib/data';
import { apiFetch, postJson, errorMessage } from '../../lib/api';
import { bpsToPercent, bpsToPercentLabel, percentToBps } from '../../lib/tax';
import { CUSTOM_TAX_PRESET, presetForRate, presets, rateBpsForPreset } from '../../lib/tax-presets';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';

const TAX_PRESETS = presets();
const TAX_LOOKUP_URL = 'https://www.avalara.com/taxrates/en/calculator.html';

type StorefrontMode = 'menu_ordering' | 'menu_links' | 'links_only';

const STOREFRONT_MODES: ReadonlyArray<{ value: StorefrontMode; label: string }> = [
  { value: 'menu_ordering', label: 'Menu + online ordering' },
  { value: 'menu_links', label: 'Menu + order links' },
  { value: 'links_only', label: 'Links only (no menu)' },
];

function storefrontModeFor(menuEnabled: boolean, orderingEnabled: boolean): StorefrontMode {
  if (!menuEnabled) return 'links_only';
  return orderingEnabled ? 'menu_ordering' : 'menu_links';
}

function storefrontFieldsFor(mode: StorefrontMode): { menu_enabled: boolean; ordering_enabled: boolean } {
  switch (mode) {
    case 'menu_ordering':
      return { menu_enabled: true, ordering_enabled: true };
    case 'menu_links':
      return { menu_enabled: true, ordering_enabled: false };
    case 'links_only':
      return { menu_enabled: false, ordering_enabled: false };
  }
}

export function PaymentsPage() {
  const { location: workspaceLocation, restaurant, canOwn } = useWorkspace();
  const { refresh } = useAuth();
  const { toast } = useToast();
  const state = useAsync(() => (workspaceLocation ? getLocation(workspaceLocation.id) : Promise.resolve(null)), [
    workspaceLocation?.id,
  ]);
  const stripe = useAsync(
    () =>
      restaurant
        ? apiFetch<StripeConnectResponse>(
            `/api/platform/stripe/status?restaurant_id=${encodeURIComponent(restaurant.id)}`,
          )
        : Promise.resolve(null),
    [restaurant?.id],
  );

  const [mode, setMode] = useState<StorefrontMode>('menu_ordering');
  const [payInStore, setPayInStore] = useState(false);
  const [taxPercent, setTaxPercent] = useState('0');
  const [presetCode, setPresetCode] = useState(CUSTOM_TAX_PRESET);
  const [prepTime, setPrepTime] = useState(15);
  const [maxItems, setMaxItems] = useState(30);
  const [dailyCap, setDailyCap] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const location = state.data;

  useEffect(() => {
    if (!location) return;
    // `menu_enabled` is part of the frozen shared type; treat a missing value
    // (a legacy row) as "menu shown" rather than silently hiding it.
    setMode(storefrontModeFor(location.menu_enabled !== false, location.ordering_enabled));
    setPayInStore(location.pay_in_store_enabled);
    const preset = presetForRate(location.tax_rate_bps);
    const presetRate = rateBpsForPreset(preset);
    setPresetCode(preset);
    setTaxPercent(bpsToPercent(presetRate ?? location.tax_rate_bps));
    setPrepTime(location.prep_time_minutes);
    setMaxItems(location.max_items_per_order);
    setDailyCap(location.in_store_daily_cap);
  }, [location]);

  if (!workspaceLocation) {
    return <EmptyState icon={<CreditCard className="h-6 w-6" />} title="Select a location" />;
  }
  if (state.loading || !location) {
    return <LoadingBlock />;
  }

  const bps = percentToBps(taxPercent);
  const totalLabel = bps === null ? '—' : bpsToPercentLabel(bps);

  const externalLinks = (location.external_order_links ?? {}) as ExternalOrderLinks;
  const hasExternalLinks = Boolean(
    externalLinks.uber_eats || externalLinks.doordash || externalLinks.grubhub,
  );

  const selectPreset = (code: string) => {
    setPresetCode(code);
    const rate = rateBpsForPreset(code);
    // Custom keeps whatever the owner typed; a known rate fills the total.
    if (rate !== null) setTaxPercent(bpsToPercent(rate));
  };

  const changeTaxRate = (value: string) => {
    setTaxPercent(value);
    // Keep the preset select honest: it shows the matching common rate, or
    // Custom as soon as the owner types their own number.
    setPresetCode(presetForRate(percentToBps(value)));
  };

  const save = async () => {
    if (bps === null) {
      setError('Enter a tax rate between 0 and 100.');
      return;
    }
    // tax_rate_bps is an integer column; half a basis point (0.005%) matters
    // less than a cent on any real order.
    const storefront = storefrontFieldsFor(mode);
    setSaving(true);
    setError(null);
    try {
      await updateLocation(location.id, {
        menu_enabled: storefront.menu_enabled,
        ordering_enabled: storefront.ordering_enabled,
        pay_in_store_enabled: payInStore,
        tax_rate_bps: Math.round(bps),
        prep_time_minutes: prepTime,
        max_items_per_order: maxItems,
        in_store_daily_cap: dailyCap,
      });
      toast({ title: 'Ordering settings saved', variant: 'success' });
      state.reload();
      void refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const connect = async () => {
    if (!restaurant) return;
    setConnecting(true);
    setError(null);
    try {
      const response = await postJson<StripeConnectResponse>('/api/platform/stripe/connect', {
        restaurant_id: restaurant.id,
      });
      if (response.url) window.open(response.url, '_blank', 'noopener,noreferrer');
      stripe.reload();
      void refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Ordering & payments" description="Control how customers can order and pay." />

      {error ? <Alert>{error}</Alert> : null}

      <Card>
        <CardHeader>
          <CardTitle>Ordering</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select
            label="Storefront mode"
            value={mode}
            onChange={(event) => setMode(event.target.value as StorefrontMode)}
            hint="What guests see on your storefront."
          >
            {STOREFRONT_MODES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {mode !== 'menu_ordering' && !hasExternalLinks ? (
            <Alert variant="error">
              Add an{' '}
              <Link to="/dashboard/visibility" className="font-medium underline">
                Uber Eats/DoorDash link
              </Link>{' '}
              or guests have no way to order.
            </Alert>
          ) : null}
          <Checkbox
            label="Allow pay in store"
            checked={payInStore}
            onChange={(event) => setPayInStore(event.target.checked)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Common rates"
              value={presetCode}
              onChange={(event) => selectPreset(event.target.value)}
            >
              {TAX_PRESETS.map((preset) => (
                <option key={preset.code || 'custom'} value={preset.code}>
                  {preset.label}
                </option>
              ))}
            </Select>
            <Input
              label="Tax rate (%)"
              value={taxPercent}
              inputMode="decimal"
              onChange={(event) => changeTaxRate(event.target.value)}
              hint="The total rate your guest pays."
            />
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700 sm:col-span-2">
              Tax collected at checkout:{' '}
              <strong className="text-zinc-900">{totalLabel}</strong>
            </div>
            <p className="text-xs text-zinc-500 sm:col-span-2">
              Rates change and vary by address —{' '}
              <a
                className="font-medium underline"
                href={TAX_LOOKUP_URL}
                target="_blank"
                rel="noreferrer"
              >
                look up your exact rate
              </a>{' '}
              and type it above. Meals can also carry a local restaurants tax on top of sales tax.
            </p>
            <Input
              label="Prep time (minutes)"
              type="number"
              min={0}
              value={prepTime}
              onChange={(event) => setPrepTime(Number(event.target.value))}
            />
            <Input
              label="Max items per order"
              type="number"
              min={1}
              value={maxItems}
              onChange={(event) => setMaxItems(Number(event.target.value))}
            />
            <Input
              label="In-store daily cap"
              type="number"
              min={0}
              value={dailyCap}
              onChange={(event) => setDailyCap(Number(event.target.value))}
              hint="0 = unlimited."
            />
          </div>
          <Button loading={saving} onClick={() => void save()}>
            Save ordering settings
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Stripe card payments</CardTitle>
            {stripe.data ? (
              stripe.data.charges_enabled ? (
                <Badge variant="success">Connected</Badge>
              ) : stripe.data.connected ? (
                <Badge variant="warning">Onboarding incomplete</Badge>
              ) : (
                <Badge variant="default">Not connected</Badge>
              )
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {stripe.loading ? (
            <LoadingBlock />
          ) : (
            <>
              <p className="text-sm text-zinc-600">
                Card payments are processed by Stripe on your own account. Money goes directly to your bank
                account; adop never holds your funds.
              </p>
              {!canOwn ? (
                <p className="text-sm text-zinc-500">Only the owner can change payment settings.</p>
              ) : (
                <Button loading={connecting} onClick={() => void connect()}>
                  <Wallet className="h-4 w-4" />
                  {stripe.data?.connected ? 'Continue onboarding' : 'Connect Stripe'}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
